import { Box, Text, Grid, GridItem, HStack } from '@chakra-ui/react';

import { useRematchSelector } from 'models/rematchStore';
import { GridItemCentered } from './GridItemCentered';
import { GridItemEmpty } from './GridItemEmpty';
import { GridItemHeaderText } from './GridItemHeaderText';
import { get, groupBy, head } from 'lodash';
import { TurbineStatusCount } from './TurbineStatusCount';
import { SubstationGridItems } from './SubstationGridItems';
import { WS_STATUSES } from './Commercial.constants';
import Moment from 'react-moment';
import { convertToBST, tzFormat } from 'common/dates';
import { SystemStatuses } from '../../../components/PortfolioShared/SystemStatuses';
import { useParkWebsocket } from '../useParkWebsocket';
import { WebSocketTypes } from 'common/webSocket.parser';
import { ConnectionLost } from 'components/PortfolioShared/ConnectionLost';
import { sortByDestination, turbineHasAnyActivities } from 'routes/OffshoreActivity/OffshoreActivity.utils';
import { OffshoreTurbineBox } from 'routes/OffshoreActivity/components/OffshoreTurbineBox';
import { useMemo } from 'react';

const loadWebsocketCommands = [
    WebSocketTypes.TURBINE,
    WebSocketTypes.GRID_CONNECTION,
    WebSocketTypes.FEED_STATUS,
    WebSocketTypes.HISTORICAL_PRODUCTION_FORECAST,
    WebSocketTypes.POWER_PRICE,
];

export const CommercialParkItem = ({ parkName }) => {
    const parkConfigs = useRematchSelector((state) => state.parksData.parkConfigs);
    const turbinesById = useRematchSelector((state) => state.parksData.turbinesById);
    const powerGridsById = useRematchSelector((state) => state.parksData.powerGridsById);

    const connected = useParkWebsocket({ parkName, commands: loadWebsocketCommands });

    const parkConfig = parkConfigs[parkName] || {};
    const parkTurbinesById = turbinesById[parkName] || {};
    const parkPowerGridsById = powerGridsById[parkName] || {};
    const transferById = useRematchSelector((state) => state.parksData.transferById);
    const parkTransfers = useMemo(() => Object.values(transferById[parkName] || {}), [transferById, parkName]);

    const byStatus = groupBy(Object.values(parkTurbinesById), 'status');

    const filteredTurbines = useMemo(
        () =>
            Object.values(parkTurbinesById)
                .filter(turbineHasAnyActivities(parkName, parkTransfers))
                .sort(sortByDestination(parkName, parkTransfers)),
        [parkTurbinesById, parkName, parkTransfers]
    );

    const substationCount = Object.keys(parkPowerGridsById).length;

    const isLoading = !substationCount;

    const loadingItemsCount = 4;

    const parkForecastHistory = useRematchSelector((state) => get(state.parksData, `forecastHistoryById[${parkName}]`));

    const forecastPowerTime = head(head(Object.values(parkForecastHistory || {}))?.active_power?.times);

    const gap = 1;
    const rowHeight = 45;
    // 1Row, 2Row, 4 rows and caps between
    const maxHeight = 30 + 25 + rowHeight * 4 + 5 * gap;

    return (
        <Box bg="grayDark2" borderRadius={4} data-testid={`commercial-park-item-${parkName}`}>
            <Grid bg="black" templateColumns="430px 1fr" gap={`${gap}px`} borderRadius={4} alignContent="start">
                <Grid
                    templateColumns="90px 80px 80px 1fr"
                    gridTemplateRows={`30px 25px repeat(${
                        isLoading ? loadingItemsCount : substationCount
                    }, ${rowHeight}px) 1fr`}
                    gridRowEnd="auto"
                    gap={`${gap}px`}
                    alignContent="start">
                    {/* Header */}
                    <GridItem bg="grayDark2" colSpan={4} display="flex" borderTopLeftRadius={4}>
                        <HStack width="100%">
                            <Text color="mustardLight" fontSize="lg" ml={2} fontWeight="bold" casing="capitalize">
                                {parkConfig?.readableName}
                            </Text>
                            <Box flex="1" />
                            <Box pr={2}>
                                <SystemStatuses parkName={parkName} />
                            </Box>
                            {!connected && <ConnectionLost />}
                        </HStack>
                    </GridItem>
                    <GridItemCentered>
                        <GridItemHeaderText>Substation</GridItemHeaderText>
                    </GridItemCentered>
                    <GridItemCentered>
                        <GridItemHeaderText>Actual</GridItemHeaderText>
                    </GridItemCentered>
                    <GridItemCentered>
                        <GridItemHeaderText>
                            PN @{' '}
                            {forecastPowerTime ? (
                                <Moment
                                    format="HH:mm"
                                    title={tzFormat(forecastPowerTime, 'dddd, MMMM Do YYYY [at] HH:mm')}>
                                    {convertToBST(forecastPowerTime)}
                                </Moment>
                            ) : (
                                '-'
                            )}
                        </GridItemHeaderText>
                    </GridItemCentered>
                    <GridItemCentered>
                        <GridItemHeaderText>Difference</GridItemHeaderText>
                    </GridItemCentered>

                    {/* Substations */}

                    {!isLoading &&
                        Object.values(parkPowerGridsById).map((powerGrid) => (
                            <SubstationGridItems
                                key={powerGrid.entityKey}
                                parkName={parkName}
                                powerGrid={powerGrid}
                                isLastItem={false}
                            />
                        ))}

                    {isLoading &&
                        [...Array(loadingItemsCount).keys()].map(() => (
                            <GridItemEmpty colSpan={4} borderBottomLeftRadius={4} />
                        ))}
                    {/* Empty row that expands and fills with gray */}
                    <GridItemEmpty colSpan={4} borderBottomLeftRadius={4} />
                </Grid>
                <Box maxHeight={maxHeight} overflow="hidden" borderRightRadius={4} className="aaa">
                    <Box maxHeight={maxHeight} overflowY="auto" height="100%">
                        <GridItemCentered
                            px={3}
                            colSpan={2}
                            justifyContent="flex-end"
                            minHeight={rowHeight}
                            borderBottom="1px solid black">
                            <HStack gap={6}>
                                <TurbineStatusCount byStatus={byStatus} status={WS_STATUSES.IN_SERVICE} />
                                <TurbineStatusCount byStatus={byStatus} status={WS_STATUSES.AVAILABLE} />
                                <TurbineStatusCount byStatus={byStatus} status={WS_STATUSES.STOPPED} />
                                <TurbineStatusCount byStatus={byStatus} status={WS_STATUSES.STOPPED_MANUAL} />
                                <TurbineStatusCount byStatus={byStatus} status={WS_STATUSES.NO_COMM} />
                            </HStack>
                        </GridItemCentered>
                        <Grid
                            gridTemplateColumns="repeat(auto-fill, minmax(min-content, 120px))"
                            gap={2}
                            bg="grayDark2"
                            borderTopRightRadius={4}
                            borderBottomRightRadius={4}
                            padding={2}
                            alignContent="start"
                            minHeight="100%">
                            {filteredTurbines.map((turbineData) => (
                                <OffshoreTurbineBox
                                    key={turbineData.entityKey}
                                    turbineData={turbineData}
                                    parkName={parkName}
                                />
                            ))}
                        </Grid>
                    </Box>
                </Box>
            </Grid>
        </Box>
    );
};
